/**
 * PDF Service for Invoice Generation
 * Uses jsPDF for client-side PDF generation
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

class PDFService {
  /**
   * Generate HealeHub-styled invoice PDF and return as base64 string
   */
  async generateHealeHubInvoicePDF(invoice, clientData, tenantData) {
    try {
      // Create a temporary DOM element with HealeHub invoice design
      const invoiceHTML = this.createInvoiceHTML(invoice, clientData, tenantData);
      
      // Create temporary container
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = invoiceHTML;
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '800px';
      tempContainer.style.backgroundColor = 'white';
      document.body.appendChild(tempContainer);
      
      // Generate PDF from HTML
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: tempContainer.scrollHeight
      });
      
      // Remove temporary element
      document.body.removeChild(tempContainer);
      
      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      
      return pdf.output('datauristring').split(',')[1]; // Return base64 only
    } catch (error) {
      console.error('Error generating HealeHub invoice PDF:', error);
      throw new Error(`PDF generation failed: ${error.message}`);
    }
  }
  
  /**
   * Create HealeHub invoice HTML template
   */
  createInvoiceHTML(invoice, clientData, tenantData) {
    const formatCurrency = (amount, currency = 'GBP') => {
      const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'SGD' ? 'S$' : currency;
      return `${symbol}${parseFloat(amount || 0).toFixed(2)}`;
    };
    
    const formatDate = (dateValue) => {
      if (!dateValue) return "";
      let date;
      if (dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        date = new Date(dateValue);
      }
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };
    
    return `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 48px; background: white; width: 800px; box-sizing: border-box;">
        <!-- Header Section -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px;">
          <!-- Left: Clinic Logo and Name -->
          <div style="display: flex; align-items: center;">
            <div>
              <h1 style="font-size: 24px; font-weight: bold; color: #111827; margin: 0;">
                ${tenantData?.name || 'HealeHub Clinic'}
              </h1>
              <p style="font-size: 14px; color: #6B7280; margin: 4px 0 0 0;">Healthcare Services</p>
            </div>
          </div>
          
          <!-- Right: Clinic Address -->
          <div style="text-align: right; font-size: 14px; color: #6B7280;">
            ${tenantData?.address ? `
              <div>${tenantData.address}</div>
              ${tenantData.phone ? `<div>Tel: ${tenantData.phone}</div>` : ''}
              ${tenantData.email ? `<div>Email: ${tenantData.email}</div>` : ''}
            ` : ''}
          </div>
        </div>
        
        <!-- Invoice Title and Details -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px;">
          <div>
            <h2 style="font-size: 32px; font-weight: bold; color: #111827; margin: 0;">INVOICE</h2>
            <div style="margin-top: 8px;">
              <div style="font-size: 14px; color: #6B7280;">Invoice #: <strong>${invoice.invoiceNumber}</strong></div>
              <div style="font-size: 14px; color: #6B7280;">Date: <strong>${formatDate(invoice.date)}</strong></div>
            </div>
          </div>
          
          <div style="text-align: right;">
            <span style="display: inline-block; padding: 4px 12px; border-radius: 6px; font-size: 12px; font-weight: 600; text-transform: uppercase; background-color: ${invoice.status === 'paid' ? '#10B981' : '#EF4444'}; color: white;">
              ${invoice.status || 'UNPAID'}
            </span>
          </div>
        </div>
        
        <!-- Bill To and Appointment Details -->
        <div style="display: flex; justify-content: space-between; margin-bottom: 32px;">
          <div style="flex: 1; margin-right: 32px;">
            <h3 style="font-size: 16px; font-weight: 600; color: #111827; margin: 0 0 8px 0;">Bill To:</h3>
            <div style="font-size: 14px; color: #374151;">
              ${clientData?.name || invoice.clientName}
            </div>
          </div>
          
          <div style="flex: 1;">
            <h3 style="font-size: 16px; font-weight: 600; color: #111827; margin: 0 0 8px 0;">Appointment Details:</h3>
            <div style="font-size: 14px; color: #374151;">
              <div>Date: ${formatDate(invoice.appointmentDate || invoice.date)}</div>
              <div>Time: ${invoice.appointmentTime || '08:00'}</div>
              <div>Service: ${invoice.service || 'Physiotherapy'}</div>
            </div>
          </div>
        </div>
        
        <!-- Invoice Items Table -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 32px;">
          <thead>
            <tr style="background-color: #F3F4F6;">
              <th style="padding: 12px; text-align: left; font-size: 14px; font-weight: 600; color: #374151; border-bottom: 1px solid #E5E7EB;">Description</th>
              <th style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #374151; border-bottom: 1px solid #E5E7EB;">Date</th>
              <th style="padding: 12px; text-align: right; font-size: 14px; font-weight: 600; color: #374151; border-bottom: 1px solid #E5E7EB;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${(invoice.lineItems || []).map(item => `
              <tr>
                <td style="padding: 12px; font-size: 14px; color: #374151; border-bottom: 1px solid #F3F4F6;">${item.description}</td>
                <td style="padding: 12px; text-align: center; font-size: 14px; color: #374151; border-bottom: 1px solid #F3F4F6;">${formatDate(item.date)}</td>
                <td style="padding: 12px; text-align: right; font-size: 14px; color: #374151; border-bottom: 1px solid #F3F4F6;">${formatCurrency(item.amount, invoice.currency)}</td>
              </tr>
            `).join('')}
            
            <!-- Totals Section -->
            <tr>
              <td colspan="2" style="padding: 12px; text-align: right; font-size: 14px; font-weight: 600; color: #374151;">Subtotal:</td>
              <td style="padding: 12px; text-align: right; font-size: 14px; font-weight: 600; color: #374151;">${formatCurrency(invoice.subtotal, invoice.currency)}</td>
            </tr>
            <tr>
              <td colspan="2" style="padding: 12px; text-align: right; font-size: 16px; font-weight: bold; color: #111827; background-color: #F3F4F6;">Total:</td>
              <td style="padding: 12px; text-align: right; font-size: 16px; font-weight: bold; color: #111827; background-color: #F3F4F6;">${formatCurrency(invoice.total, invoice.currency)}</td>
            </tr>
          </tbody>
        </table>
        
        <!-- Payment Information and Invoice Details -->
        <div style="display: flex; justify-content: space-between; margin-bottom: 32px;">
          <div style="flex: 1; margin-right: 32px;">
            <h3 style="font-size: 16px; font-weight: 600; color: #111827; margin: 0 0 8px 0;">Payment Information:</h3>
            <div style="font-size: 14px; color: #374151;">
              <div>Payment Type: Not specified</div>
            </div>
          </div>
          
          <div style="flex: 1;">
            <h3 style="font-size: 16px; font-weight: 600; color: #111827; margin: 0 0 8px 0;">Invoice Details:</h3>
            <div style="font-size: 14px; color: #374151;">
              <div>Issued by: System</div>
              <div>Created: ${formatDate(invoice.createdAt || invoice.date)}</div>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div style="border-top: 1px solid #E5E7EB; padding-top: 16px; font-size: 12px; color: #6B7280;">
          <div>Payment Terms: Payment is due within 30 days of invoice date. Late payments may incur a 1.5% monthly charge.</div>
          <div style="margin-top: 4px;">Payment Methods Accepted: Bank transfer, Card payments</div>
          <div style="margin-top: 4px;">For billing inquiries or disputes, contact: ${tenantData?.email || 'healerehab@gmail.com'}</div>
        </div>
      </div>
    `;
  }
  
  /**
   * Generate invoice PDF from HTML element and return as base64 string
   */
  async generateInvoicePDFFromHTML(htmlElement) {
    try {
      // Configure html2canvas options for better quality
      const canvas = await html2canvas(htmlElement, {
        scale: 1.5, // Reduced scale to avoid oversized content
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: htmlElement.scrollWidth,
        height: htmlElement.scrollHeight,
        scrollX: 0,
        scrollY: 0
      });

      // Create PDF with A4 dimensions
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm (corrected)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const imgData = canvas.toDataURL('image/png');

      // If content fits on one page, add it normally
      if (imgHeight <= pageHeight) {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        // If content is too tall, scale it down to fit one page
        const scaledHeight = pageHeight - 10; // Leave small margin
        const scaledWidth = (canvas.width * scaledHeight) / canvas.height;
        pdf.addImage(imgData, 'PNG', (imgWidth - scaledWidth) / 2, 5, scaledWidth, scaledHeight);
      }

      // Convert to base64
      const pdfBase64 = pdf.output('datauristring').split(',')[1];
      return pdfBase64;
      
    } catch (error) {
      console.error('Error generating PDF from HTML:', error);
      throw new Error(`PDF generation failed: ${error.message}`);
    }
  }

  /**
   * Generate invoice PDF and return as base64 string (legacy method - kept for compatibility)
   */
  async generateInvoicePDFBase64(invoice, clientName, currency = 'GBP') {
    try {
      const doc = new jsPDF();
      
      // Set up fonts and colors
      const primaryColor = [59, 130, 246]; // Blue
      const textColor = [31, 41, 55]; // Dark gray
      const lightGray = [249, 250, 251];
      
      // Header
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, 210, 40, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('INVOICE', 20, 25);
      
      // Invoice details (top right)
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Invoice #: ${invoice.invoiceNumber}`, 130, 20);
      doc.text(`Date: ${invoice.date || 'N/A'}`, 130, 28);
      
      // Reset text color for body
      doc.setTextColor(...textColor);
      
      // Bill To section
      let yPos = 60;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Bill To:', 20, yPos);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(clientName, 20, yPos + 10);
      
      // Status badge
      const statusX = 130;
      const statusY = yPos - 10;
      
      // Set status color
      let statusColor;
      switch (invoice.status) {
        case 'paid':
          statusColor = [34, 197, 94]; // Green
          break;
        case 'unpaid':
          statusColor = [239, 68, 68]; // Red
          break;
        default:
          statusColor = [107, 114, 128]; // Gray
      }
      
      doc.setFillColor(...statusColor);
      doc.roundedRect(statusX, statusY, 40, 15, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(invoice.status.toUpperCase(), statusX + 5, statusY + 10);
      
      // Reset text color
      doc.setTextColor(...textColor);
      
      // Line items table
      yPos += 30;
      
      const tableData = [];
      let subtotal = 0;
      
      if (invoice.lineItems && invoice.lineItems.length > 0) {
        invoice.lineItems.forEach(item => {
          const amount = parseFloat(item.amount || 0);
          subtotal += amount;
          tableData.push([
            item.description || 'Service',
            1,
            `${currency} ${amount.toFixed(2)}`,
            `${currency} ${amount.toFixed(2)}`
          ]);
        });
      } else {
        // Fallback for invoices without line items
        const amount = parseFloat(invoice.total || 0);
        subtotal = amount;
        tableData.push([
          'Service',
          1,
          `${currency} ${amount.toFixed(2)}`,
          `${currency} ${amount.toFixed(2)}`
        ]);
      }
      
      // Add discount row if applicable
      const discount = parseFloat(invoice.discount || 0);
      if (discount > 0) {
        tableData.push([
          'Discount',
          1,
          `${currency} -${discount.toFixed(2)}`,
          `${currency} -${discount.toFixed(2)}`
        ]);
        subtotal -= discount;
      }
      
      // Add tax row if applicable
      const tax = parseFloat(invoice.tax || 0);
      if (tax > 0) {
        tableData.push([
          'Tax/VAT',
          1,
          `${currency} ${tax.toFixed(2)}`,
          `${currency} ${tax.toFixed(2)}`
        ]);
        subtotal += tax;
      }
      
      let tableResult;
      autoTable(doc, {
        startY: yPos,
        head: [['Description', 'Qty', 'Rate', 'Amount']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: primaryColor,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 11
        },
        bodyStyles: {
          fontSize: 10,
          textColor: textColor
        },
        alternateRowStyles: {
          fillColor: lightGray
        },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 20, halign: 'center' },
          2: { cellWidth: 30, halign: 'right' },
          3: { cellWidth: 30, halign: 'right' }
        },
        didDrawPage: function (data) {
          tableResult = data;
        }
      });
      
      // Total section - use fallback if previousAutoTable is not available
      const finalY = (doc.previousAutoTable?.finalY || tableResult?.cursor?.y || yPos + 50) + 10;
      const total = parseFloat(invoice.total || subtotal);
      
      doc.setFillColor(...primaryColor);
      doc.rect(130, finalY, 60, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL:', 135, finalY + 6);
      doc.text(`${currency} ${total.toFixed(2)}`, 135, finalY + 12);
      
      // Footer
      doc.setTextColor(...textColor);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const footerY = 270;
      doc.text('Thank you for your business!', 20, footerY);
      doc.text('Generated by HealeHub Practice Management System', 20, footerY + 6);
      
      // Convert to base64
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      return pdfBase64;
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error(`PDF generation failed: ${error.message}`);
    }
  }
  
  /**
   * Generate and download invoice PDF
   */
  async downloadInvoicePDF(invoice, clientName, currency = 'GBP') {
    try {
      const base64 = await this.generateInvoicePDFBase64(invoice, clientName, currency);
      
      // Create download link
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${base64}`;
      link.download = `Invoice_${invoice.invoiceNumber || 'invoice'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      return true;
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }
  
  /**
   * Format date for display
   */
  formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    
    try {
      // Handle Firestore Timestamp objects
      if (dateStr.seconds) {
        dateStr = new Date(dateStr.seconds * 1000);
      }
      
      const date = new Date(dateStr);
      if (isNaN(date)) return 'N/A';
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  }
}

export default new PDFService();
